import { createSlice } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";
import { AppDispatch } from "../store";
import { alertMessage } from "../actions/common";
import { errorHandler } from "app/shared/Service/errorHandler";
import {
  getCatalogImportLogDetailAPI,
  getCatalogImportLogsAPI,
} from "app/services/catalogs";
import {
  CatalogImportLog,
  CatalogImportLogDetail,
  CatalogImportLogStatusFilter,
  CatalogImportLogUser,
  CatalogImportPortion,
} from "app/types/catalog-import";
import { PaginationInterfaceLF } from "app/types";

interface CatalogImportLogsState {
  logs: CatalogImportLog[];
  users: CatalogImportLogUser[];
  pagination: PaginationInterfaceLF | null;
  statusFilter: CatalogImportLogStatusFilter;
  isLoading: boolean;
  isDetailLoading: boolean;
  selectedLogDetail: CatalogImportLogDetail | null;
  detailPortions: CatalogImportPortion[];
}

const initialState: CatalogImportLogsState = {
  logs: [],
  users: [],
  pagination: null,
  statusFilter: "All",
  isLoading: false,
  isDetailLoading: false,
  selectedLogDetail: null,
  detailPortions: [],
};

export const catalogImportLogsSlice = createSlice({
  name: "catalogImportLogs",
  initialState,
  reducers: {
    setImportLogs: (state, action: PayloadAction<CatalogImportLog[]>) => {
      state.logs = action.payload;
    },
    setImportLogUsers: (
      state,
      action: PayloadAction<CatalogImportLogUser[]>
    ) => {
      state.users = action.payload;
    },
    setImportLogsPagination: (
      state,
      action: PayloadAction<PaginationInterfaceLF | null>
    ) => {
      state.pagination = action.payload;
    },
    setImportLogsStatusFilter: (
      state,
      action: PayloadAction<CatalogImportLogStatusFilter>
    ) => {
      state.statusFilter = action.payload;
    },
    setImportLogsLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setImportLogDetailLoading: (state, action: PayloadAction<boolean>) => {
      state.isDetailLoading = action.payload;
    },
    setSelectedLogDetail: (
      state,
      action: PayloadAction<CatalogImportLogDetail | null>
    ) => {
      state.selectedLogDetail = action.payload;
    },
    setDetailPortions: (
      state,
      action: PayloadAction<CatalogImportPortion[]>
    ) => {
      state.detailPortions = action.payload;
    },
    resetCatalogImportLogs: () => initialState,
  },
});

export const {
  setImportLogs,
  setImportLogUsers,
  setImportLogsPagination,
  setImportLogsStatusFilter,
  setImportLogsLoading,
  setImportLogDetailLoading,
  setSelectedLogDetail,
  setDetailPortions,
  resetCatalogImportLogs,
} = catalogImportLogsSlice.actions;

export default catalogImportLogsSlice.reducer;

type GetCatalogImportLogsActionPayload = {
  catalogId: number;
  status?: CatalogImportLogStatusFilter;
  continutationToken?: string | null;
};

export const getCatalogImportLogsAction =
  ({
    catalogId,
    status,
    continutationToken,
  }: GetCatalogImportLogsActionPayload) =>
  async (dispatch: AppDispatch) => {
    dispatch(setImportLogsLoading(true));
    if (status !== undefined) {
      dispatch(setImportLogsStatusFilter(status));
    }

    try {
      const effectiveStatus = status ?? "All";
      const { data } = await getCatalogImportLogsAPI(catalogId, {
        status: effectiveStatus === "All" ? undefined : effectiveStatus,
        limit: 50,
        continutationToken,
      });

      dispatch(setImportLogs(data.data ?? []));
      dispatch(setImportLogUsers(data.included?.users ?? []));
      dispatch(setImportLogsPagination(data.pagination ?? null));
    } catch (error) {
      dispatch(setImportLogs([]));
      dispatch(setImportLogUsers([]));
      dispatch(setImportLogsPagination(null));
      const message = errorHandler(error);
      dispatch(alertMessage(message, "error"));
    } finally {
      dispatch(setImportLogsLoading(false));
    }
  };

type GetCatalogImportLogDetailActionPayload = {
  catalogId: number;
  logId: number;
};

export const getCatalogImportLogDetailAction =
  ({ catalogId, logId }: GetCatalogImportLogDetailActionPayload) =>
  async (dispatch: AppDispatch) => {
    dispatch(setImportLogDetailLoading(true));
    dispatch(setSelectedLogDetail(null));

    try {
      const { data } = await getCatalogImportLogDetailAPI(catalogId, logId);
      dispatch(setSelectedLogDetail(data.data));
      dispatch(setImportLogUsers(data.included?.users ?? []));
      dispatch(setDetailPortions(data.included?.portions ?? []));
    } catch (error) {
      const message = errorHandler(error);
      dispatch(alertMessage(message, "error"));
    } finally {
      dispatch(setImportLogDetailLoading(false));
    }
  };

export const resetCatalogImportLogsAction = () => (dispatch: AppDispatch) => {
  dispatch(resetCatalogImportLogs());
};

export const clearSelectedLogDetailAction = () => (dispatch: AppDispatch) => {
  dispatch(setSelectedLogDetail(null));
  dispatch(setDetailPortions([]));
};
